import { STRIPE_PROXY_URL } from '../config/billing';

const API = STRIPE_PROXY_URL;

const authHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('intrlock_token') || ''}`,
});

export const stripeFetch = async (pathOrUrl: string, init: RequestInit = {}): Promise<Response> => {
  const url = /^https?:\/\//i.test(pathOrUrl)
    ? pathOrUrl
    : `${API}/${pathOrUrl.replace(/^\//, '')}`;
  const res = await fetch(url, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Stripe API error ${res.status}: ${detail || res.statusText}`);
  }
  return res;
};

export const stripeDashboardSubscriptionUrl = (subscription: { id: string; livemode?: boolean }): string =>
  `https://dashboard.stripe.com${subscription.livemode === false ? '/test' : ''}/subscriptions/${encodeURIComponent(subscription.id)}`;

const post = async (path: string, body: Record<string, unknown>) => {
  const res = await stripeFetch(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.json();
};

const get = async (path: string, params: Record<string, string>) => {
  const qs = new URLSearchParams(params).toString();
  const res = await stripeFetch(`${path}?${qs}`);
  return res.json();
};

const del = async (path: string, body: Record<string, unknown>) => {
  const res = await stripeFetch(path, {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
  return res.json();
};

export const stripeApi = {
  // Customer management
  createCustomer: (email: string, name: string, metadata?: Record<string, string>) =>
    post('customers', { email, name, metadata }),

  // Setup intents
  createSetupIntent: (customerId: string) =>
    post('setup-intent', { customer_id: customerId }),

  // Payment methods
  listPaymentMethods: (customerId: string) =>
    get('payment-methods', { customer_id: customerId }),
  detachPaymentMethod: (paymentMethodId: string) =>
    del('payment-methods', { payment_method_id: paymentMethodId }),

  // Subscription management (new multi-item endpoints)
  createSubscription: (
    customerId: string,
    priceIds: Array<{ price: string; quantity: number }>,
    metadata?: Record<string, string>
  ) =>
    post('subscriptions/create', { customer_id: customerId, price_ids: priceIds, metadata }),

  updateSubscription: (subscriptionId: string, items: Array<{ id: string; quantity: number }>) =>
    post('subscriptions/update', { subscription_id: subscriptionId, items }),

  cancelSubscription: (subscriptionId: string) =>
    post('subscriptions/cancel', { subscription_id: subscriptionId }),

  getSubscriptionDetail: (subscriptionId: string) =>
    get('subscriptions/detail', { subscription_id: subscriptionId }),

  // Legacy endpoints
  listSubscriptions: (customerId: string) =>
    get('subscriptions', { customer_id: customerId }),

  // Invoices
  listInvoices: (customerId: string) =>
    get('invoices', { customer_id: customerId }),

  // Billing portal
  createBillingPortal: (customerId: string, returnUrl: string) =>
    post('billing-portal', { customer_id: customerId, return_url: returnUrl }),
};
